import { OtpError } from '@0x234/common-vue/errors/otp-error';
import { RequestManager } from '@jaseeey/request-manager';
import { defineStore } from 'pinia';
import { useOtpManager } from 'src/composables/otp-manager';
import { strategyConfig } from 'src/configs/strategy-config';
import { isInvalidated } from 'src/utils/store-util';
import { resetAllStores } from 'stores/index';
import { usePublicInviteLinkStore } from 'stores/public-invite-link-store';
import { useUserProfileStore } from 'stores/user-profile-store';
import { computed, ref, watch } from 'vue';

export const useAuthStore = defineStore('auth', () => {
    const otpManager = useOtpManager();
    const userProfileStore = useUserProfileStore();

    let authEventsSubscribed = false;

    const accounts = ref([]);
    const accountsDirty = ref(false);
    const accountsLoadedAt = ref(null);

    const strategies = ref([]);
    const strategiesDirty = ref(false);
    const strategiesLoadedAt = ref(null);

    const user = ref(null);
    const userDirty = ref(false);
    const userLoadedAt = ref(null);

    const userAuthenticated = computed(() => !!user.value);
    const userDisplayName = computed(() => user.value?.displayName);
    const userReady = computed(() => user.value?.isReady);

    const accountsInvalidated = computed(() => {
        return isInvalidated(accountsDirty.value, accountsLoadedAt.value);
    });

    const linkedStrategies = computed(() => {
        if (!user.value) {
            return [];
        }
        const emailStrategy = {
            id: -1,
            providerId: 'email',
            providerUsername: user.value.email,
            locked: true
        };
        return [emailStrategy, ...((accounts.value && accounts.value.length > 0) ? accounts.value : [])];
    });

    const availableStrategies = computed(() => {
        const activeStrategies = linkedStrategies.value.map(a => a.providerId);
        return strategies.value.filter(p => !activeStrategies.includes(p.id));
    });

    const sortedStrategies = computed(() => strategies.value.sort((idA, idB) => {
        if (idA.order < idB.order) return -1;
        if (idA.order > idB.order) return 1;
        return 0;
    }));

    const strategyById = computed(() => {
        const strategyMap = new Map(strategies.value.map(s => [s.id, s]));
        return providerId => strategyMap.get(providerId) || null;
    });

    async function requestLoginOtp(email, captcha = undefined) {
        const publicInviteLinkStore = usePublicInviteLinkStore();
        const data = { email };
        const headers = {};
        if (publicInviteLinkStore.inviteLinkToken) {
            headers['x-xprt-token'] = publicInviteLinkStore.inviteLinkToken;
        }
        try {
            return (await this.$auth.postWithCaptcha('/login', data, { captcha, headers })).data.token;
        } catch (err) {
            _handleOtpError(err);
        }
    }

    async function reissueOtp(authJwt) {
        const headers = { authorization: 'Bearer ' + authJwt };
        try {
            return (await this.$auth.post('/reissue-token', {}, { headers })).data.token;
        } catch (err) {
            _handleOtpError(err);
        }
    }

    async function verifyOtp(authJwt, otpToken, email) {
        const data = { email, token: otpToken.toString() };
        const headers = { authorization: 'Bearer ' + authJwt };
        try {
            await this.$auth.post('/user/verify', data, { headers });
            invalidateUser.bind(this)();
            await loadUser.bind(this)();
        } catch (err) {
            _handleOtpError(err);
        }
    }

    function _handleOtpError(err) {
        if (err.response && err.response.data.name === 'RequestLimitExceededError') {
            const retryAfter = err.response.headers['retry-after']
                ? parseInt(err.response.headers['retry-after'], 10)
                : null;
            throw new OtpError('Too many attempts, please wait a moment and try again.', {
                originalError: err,
                retryAfter
            });
        }
        if (err.response && err.response.data.name === 'TokenMismatchError') {
            throw new OtpError('Invalid code, please try again or request a new code.', {
                originalError: err
            });
        }
        throw err;
    }

    function invalidateAccounts() {
        accountsDirty.value = true;
    }

    function clearAccounts() {
        accounts.value = [];
    }

    async function loadAccounts() {
        if (!accountsInvalidated.value) {
            return;
        }
        try {
            const response = await this.$auth.get('/user/accounts');
            accounts.value = response.data;
        } finally {
            accountsDirty.value = false;
            accountsLoadedAt.value = Date.now();
        }
    }

    async function deleteAccount(accountId) {
        await this.$auth.delete(`/user/accounts/${accountId}`);
        accounts.value.splice(accounts.value.findIndex(a => a.id === accountId), 1);

    }

    function hasAccountForStrategy(strategyId) {
        return accounts.value.some(a => a.providerId === strategyId);
    }

    function invalidateStrategies() {
        strategiesDirty.value = true;
    }

    function clearStrategies() {
        strategies.value.length = 0;
    }

    async function loadStrategies() {
        if (!isInvalidated(strategiesDirty.value, strategiesLoadedAt.value)) {
            return;
        }
        return RequestManager.call(this.$auth, 'get', '/strategies', null, null, res => {
            clearStrategies.bind(this)();
            for (const r of res.data ?? []) {
                strategies.value.push({ ...r, ...strategyConfig[r.id] });
            }
            strategiesDirty.value = false;
            strategiesLoadedAt.value = Date.now();
            return strategies.value;
        });
    }

    function invalidateUser() {
        userDirty.value = true;
    }

    function setUser(_user = null) {
        user.value = _user;
        userDirty.value = false;
        userLoadedAt.value = Date.now();
    }

    function clearUser() {
        user.value = null;
        userDirty.value = false;
        userLoadedAt.value = null;
    }

    async function loadUser() {
        if (!isInvalidated(userDirty.value, userLoadedAt.value)) {
            return;
        }
        await RequestManager.call(this.$auth, 'get', '/user', null, null, async res => {
            const { accounts: accountsResource, ...userResource } = res.data;
            accounts.value = accountsResource;
            setUser(userResource);
            usePublicInviteLinkStore().inviteLinkToken = null;
            accountsDirty.value = false;
            accountsLoadedAt.value = Date.now();
        }, () => {
            setUser();
        });
    }

    async function saveUser(updatedUser) {
        const response = await this.$auth.put('/user', updatedUser);
        if (response.status === 202) return otpManager.openOtpDialog(response.data.token, updatedUser.email).onOk(async () => {
            // Reload user on successful verification.
            invalidateUser.bind(this)();
            await loadUser.bind(this)();
        });
        user.value = response.data;
    }

    async function logoutUser() {
        await this.$auth.get('/logout');
        clearUser();
    }

    /**
     * Subscribes to authentication-related global events and watches for changes in the user state. This function sets
     * up event listeners for `session:expired` and `session:unauthorized` events, and also watches the user state for
     * any changes. If the user state becomes null (indicating a logout or session expiration), it triggers a reset of
     * all stores to clear any potentially sensitive or user-specific data.
     *
     * Note: This function must be called externally from the store to ensure the correct context (`this`) is available.
     * Calling it directly within the store setup will result in `this` being undefined, as Pinia does not bind `this`
     * to the store instance in setup functions.
     */
    function subscribeToAuthEvents() {
        if (authEventsSubscribed) return;
        this.$event.addEventListener('session:expired', () => resetAllStores());
        this.$event.addEventListener('session:unauthorized', () => resetAllStores());
        watch(user, u => u || resetAllStores());
        authEventsSubscribed = true;
    }

    // Subscribe to changes on the UserProfileStore and update the photoUrl. This removes the need to reload the user
    // unnecessarily if the user updates their profile photo, as the URL is already known from the response.
    userProfileStore.$subscribe(mutation => {
        if (user.value && mutation.events && mutation.events.key === 'photoUrl' && (mutation.events.type === 'add' || mutation.events.type === 'set')) {
            user.value.photoUrl = mutation.events.newValue;
        }
    });

    return {
        accounts,
        accountsDirty,
        accountsLoadedAt,
        strategies,
        strategiesDirty,
        strategiesLoadedAt,
        user,
        userDirty,
        userLoadedAt,
        userAuthenticated,
        userDisplayName,
        userReady,
        linkedStrategies,
        availableStrategies,
        sortedStrategies,
        strategyById,
        requestLoginOtp,
        reissueOtp,
        verifyOtp,
        invalidateAccounts,
        clearAccounts,
        loadAccounts,
        deleteAccount,
        hasAccountForStrategy,
        invalidateStrategies,
        clearStrategies,
        loadStrategies,
        invalidateUser,
        clearUser,
        loadUser,
        saveUser,
        logoutUser,
        subscribeToAuthEvents
    };
});
